import React, { useState } from "react";
import {
  BooleanInput,
  Edit,
  FileInput,
  FunctionField,
  ImageInput,
  NumberInput,
  Show,
  SimpleForm,
  TextField,
  TextInput,
  useRecordContext,
} from "react-admin";
import { Typography } from "@material-ui/core";
import PlaceholderImg from "lib/assets/placeholder-image.png";
import { SVGFromUrl } from "lib/components/SVGFromUrl";
import {
  PublishEditAside,
  PublishEditToolbar,
} from "lib/components/PublishEdit";
import {
  CATEGORY_STYLE_COLOR_FALLBACK,
  CATEGORY_STYLE_COLORS,
} from "constants/categoryStyleColors";
import { getRequiredFieldErrors } from "../../utils";
import { Classes } from "./components/Classes";
import { Videos } from "./components/Videos";

const ASSET_PATH = process.env.REACT_APP_ASSET_PATH;

export function CategoryEdit(props) {
  const [isFormDirty, setIsFormDirty] = useState(false);

  return (
    <>
      <Edit
        actions={false}
        {...props}
        mutationMode="pessimistic"
        aside={
          <PublishEditAside
            config={{
              resource: "categories",
              resourceLabel: "Category",
            }}
            isFormDirty={isFormDirty}
          />
        }
      >
        <EditForm setIsFormDirty={setIsFormDirty} />
      </Edit>

      <Show {...props} actions={null}>
        <Classes />
      </Show>

      <Show {...props} actions={null}>
        <Videos />
      </Show>
    </>
  );
}

function EditForm({ setIsFormDirty, ...props }) {
  const record = useRecordContext(props);

  return (
    <SimpleForm
      {...props}
      toolbar={<PublishEditToolbar setIsFormDirty={setIsFormDirty} />}
      validate={validateEditCategory}
      warnWhenUnsavedChanges
    >
      <Typography variant="h6">Edit Category</Typography>
      <TextField source="id" label={"Database ID"} />
      {record.hidden ? (
        <TextInput source="slug" />
      ) : (
        <TextField source="slug" />
      )}
      <TextInput source="name" fullWidth />
      <TextInput source="description" fullWidth required={false} multiline />
      <NumberInput source="sortIndex" step={1} min={0} />
      <BooleanInput label="Is Onboarding" source="isOnboarding" />
      <ImageInput
        source="thumbnailImageUrl"
        multiple={false}
        label="Thumbnail"
        accept="image/jpeg"
      >
        <FunctionField
          render={(record) => {
            const url = record?.rawFile
              ? URL.createObjectURL(record.rawFile)
              : `${record}?w=300`;
            return (
              <img
                alt="thumbnail"
                src={url}
                width={300}
                onError={(e) => {
                  const { target } = e;
                  target.src = PlaceholderImg;
                }}
              />
            );
          }}
        />
      </ImageInput>

      <Typography variant="subtitle1" style={{ marginTop: 16 }}>
        Explore Styles tile image
      </Typography>
      <Typography variant="caption" color="textSecondary">
        Full-bleed SVG for the mobile/web Library &quot;Explore Styles&quot;
        tile (separate from the JPEG thumbnail above). Previewed over the
        category&apos;s mobile background color. Once saved, the new tile image
        may take a few minutes to replace the old one while the CDN cache
        refreshes.
      </Typography>

      {/* Current saved tile, driven by the category record's slug. This lives
          OUTSIDE FileInput because `tileImageUrl` is a virtual (non-backend)
          field that is never seeded, so FileInput holds no value on load and
          would never render a child preview for an already-saved tile. */}
      <SavedTilePreview slug={record?.slug} />

      {/* FileInput's child previews ONLY the newly-picked file. */}
      <FileInput
        source="tileImageUrl"
        multiple={false}
        label="Explore Styles tile image (SVG)"
        accept="image/svg+xml"
      >
        <FunctionField
          render={(fileRecord) =>
            renderPickedTilePreview(fileRecord, record?.slug)
          }
        />
      </FileInput>
    </SimpleForm>
  );
}

// 200x200 frame painted with the category's real mobile background color, so
// every tile preview is composited exactly as the Library renders it.
function TileFrame({ slug, children }) {
  const backgroundColor =
    CATEGORY_STYLE_COLORS[slug] ?? CATEGORY_STYLE_COLOR_FALLBACK;
  return (
    <div
      style={{
        width: 200,
        height: 200,
        backgroundColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </div>
  );
}

const tilePlaceholder = (
  <Typography variant="caption" style={{ color: "#fff" }}>
    No tile image yet
  </Typography>
);

// The already-saved tile, fetched from the slug-derived imgix path via
// SVGFromUrl. A missing tile (or absent slug/asset path) just shows the
// placeholder — SVGFromUrl's fallback swallows the 404 so it is never an error.
function SavedTilePreview({ slug }) {
  const savedUrl =
    slug && ASSET_PATH ? `${ASSET_PATH}/style-titles-svgs/${slug}.svg` : null;
  return (
    <TileFrame slug={slug}>
      {savedUrl ? (
        <SVGFromUrl url={savedUrl} fallback={tilePlaceholder} />
      ) : (
        tilePlaceholder
      )}
    </TileFrame>
  );
}

// FileInput child: previews ONLY the just-picked file via a local object URL,
// composited over the CATEGORY's background color (passed in — the per-file
// record FileInput puts in context, `{ rawFile }`, has no slug).
function renderPickedTilePreview(fileRecord, slug) {
  if (!fileRecord?.rawFile) {
    return null;
  }
  return (
    <TileFrame slug={slug}>
      <img
        alt="Explore Styles tile preview"
        src={URL.createObjectURL(fileRecord.rawFile)}
        style={{ width: "100%", height: "100%" }}
      />
    </TileFrame>
  );
}

function validateEditCategory(values) {
  return getRequiredFieldErrors(values, ["slug", "name"]);
}
